/**
 * Ricerca, filtri e ordinamento della watchlist (TODO.md).
 *
 * Tutto lato client: la lista è già interamente in memoria (poche decine di
 * righe), quindi un round-trip al server per riordinare aggiungerebbe latenza
 * senza togliere lavoro a nessuno. Logica pura, senza React, così è verificabile
 * senza montare la pagina.
 *
 * ⚠️ Prezzi e variazioni sono STRINGHE decimali (ADR-0006): `Number()` compare
 * qui solo per stabilire un *ordine*, mai per calcolare o mostrare un valore.
 */

import type { WatchlistItem } from './api';

export type SortKey = 'name' | 'lastPrice' | 'deltaPct' | 'addedPrice' | 'lastPriceAt' | 'addedAt';
export type SortDirection = 'asc' | 'desc';
export type PortfolioFilter = 'all' | 'in' | 'out';

export type WatchlistView = {
  query: string;
  portfolio: PortfolioFilter;
  /** MIC del mercato, `''` per "tutti"; `'none'` isola i titoli senza mercato risolto. */
  mic: string;
  sortKey: SortKey;
  sortDirection: SortDirection;
};

export const DEFAULT_VIEW: WatchlistView = {
  query: '',
  portfolio: 'all',
  mic: '',
  sortKey: 'name',
  sortDirection: 'asc',
};

/** Un click su una colonna già attiva inverte il verso, su una nuova riparte dal verso naturale. */
export function toggleSort(view: WatchlistView, key: SortKey): WatchlistView {
  if (view.sortKey === key) {
    return { ...view, sortDirection: view.sortDirection === 'asc' ? 'desc' : 'asc' };
  }
  // Il nome si legge dall'alto in ordine alfabetico; tutto il resto (prezzi,
  // variazioni, date) interessa dal valore più alto o più recente in giù.
  return { ...view, sortKey: key, sortDirection: key === 'name' ? 'asc' : 'desc' };
}

function matchesQuery(item: WatchlistItem, needle: string): boolean {
  const haystack = [
    item.instrument.name,
    item.instrument.isin,
    item.instrument.symbol,
    item.instrument.marketMic,
    item.notes,
  ];
  return haystack.some((value) => value !== null && value.toLowerCase().includes(needle));
}

/**
 * Il valore usato per ordinare. Un dato assente non è "zero": un titolo senza
 * quotazione finirebbe in mezzo ai ribassi, dando l'impressione di un crollo.
 * `null` viene invece spinto in fondo in entrambi i versi.
 */
function sortValue(item: WatchlistItem, key: SortKey): number | string | null {
  switch (key) {
    case 'name':
      return item.instrument.name.toLowerCase();
    case 'lastPrice':
      return numeric(item.lastPriceBase ?? item.lastPrice);
    case 'addedPrice':
      return numeric(item.addedPriceBase ?? item.addedPrice);
    case 'deltaPct':
      return numeric(item.deltaPct);
    case 'lastPriceAt':
      return item.lastPriceAt ? Date.parse(item.lastPriceAt) : null;
    case 'addedAt':
      return Date.parse(item.addedAt);
  }
}

function numeric(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function availableMics(items: WatchlistItem[]): string[] {
  const mics = new Set<string>();
  for (const item of items) {
    if (item.instrument.marketMic) mics.add(item.instrument.marketMic);
  }
  return [...mics].sort();
}

export function applyWatchlistView(items: WatchlistItem[], view: WatchlistView): WatchlistItem[] {
  const needle = view.query.trim().toLowerCase();

  const filtered = items.filter((item) => {
    if (needle && !matchesQuery(item, needle)) return false;
    if (view.portfolio === 'in' && !item.inPortfolio) return false;
    if (view.portfolio === 'out' && item.inPortfolio) return false;
    if (view.mic === 'none' && item.instrument.marketMic !== null) return false;
    if (view.mic && view.mic !== 'none' && item.instrument.marketMic !== view.mic) return false;
    return true;
  });

  const sign = view.sortDirection === 'asc' ? 1 : -1;
  return filtered.sort((a, b) => {
    const left = sortValue(a, view.sortKey);
    const right = sortValue(b, view.sortKey);
    if (left === null && right === null) return 0;
    if (left === null) return 1; // i dati mancanti restano in coda in entrambi i versi
    if (right === null) return -1;
    if (left === right) return 0;
    return left < right ? -sign : sign;
  });
}
